import { justDate, isoDateString } from './date-utils';
import { getHolidaySet } from './holiday-calculator';
import { generateWorkdays } from './work-day-calculator';

/**
 * Unified job date resolver - handles both new workDays model and legacy scheduledDate + duration
 * Jobs display on their exact scheduled dates - holidays are visual indicators only
 */

// Get all calendar dates for a job (visible work days)
export function getJobCalendarDates(job) {
  // Priority 1: Explicit workDays (new model)
  if (job.workDays && job.workDays.length > 0) {
    return job.workDays
      .filter(wd => wd.status !== 'noWork')
      .map(wd => justDate(wd.date))
      .sort((a, b) => a - b);
  }
  
  // Priority 2: Legacy model (scheduledDate + duration)
  if (!job.scheduledDate) return [];
  
  const startDate = new Date(job.scheduledDate);
  const duration = Math.max(job.durationDays || 1, 1);
  
  // Generate workdays - only skip weekends, NOT holidays
  // User controls if they work on holidays, so jobs show on exact scheduled dates
  const dates = [];
  let current = new Date(startDate);
  
  while (dates.length < duration) {
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Only skip weekends, not holidays (user decides if they work holidays)
    if (!isWeekend) {
      dates.push(justDate(current));
    }
    
    // Move to next day
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

// Get effective start date for a job
export function getJobStartDate(job) {
  // Priority 1: Explicit workDays
  if (job.workDays && job.workDays.length > 0) {
    const workDates = job.workDays
      .filter(wd => wd.status !== 'noWork')
      .map(wd => justDate(wd.date))
      .sort((a, b) => a - b);
    
    return workDates[0] || null;
  }
  
  // Priority 2: scheduledDate
  return job.scheduledDate ? justDate(job.scheduledDate) : null;
}

// Get effective end date for a job
export function getJobEndDate(job) {
  // Priority 1: Explicit workDays
  if (job.workDays && job.workDays.length > 0) {
    const workDates = job.workDays
      .filter(wd => wd.status !== 'noWork')
      .map(wd => justDate(wd.date))
      .sort((a, b) => a - b);
    
    return workDates[workDates.length - 1] || null;
  }
  
  // Priority 2: Legacy calculation
  const dates = getJobCalendarDates(job);
  return dates.length > 0 ? dates[dates.length - 1] : null;
}

// Check if job is scheduled on a specific date
export function isJobOnDate(job, date) {
  const targetISO = isoDateString(justDate(date));
  
  // Priority 1: Check explicit workDays
  if (job.workDays && job.workDays.length > 0) {
    return job.workDays.some(wd => {
      return wd.status !== 'noWork' && isoDateString(wd.date) === targetISO;
    });
  }
  
  // Priority 2: Check legacy dates
  const dates = getJobCalendarDates(job);
  return dates.some(d => isoDateString(d) === targetISO);
}

// Get work day status for a specific date
export function getWorkDayStatus(job, date) {
  if (!job.workDays || job.workDays.length === 0) return null;
  
  const targetISO = isoDateString(justDate(date));
  const workDay = job.workDays.find(wd => isoDateString(wd.date) === targetISO);
  
  return workDay ? workDay.status : null;
}

// Get work day note for a specific date
export function getWorkDayNote(job, date) {
  if (!job.workDays || job.workDays.length === 0) return null;
  
  const targetISO = isoDateString(justDate(date));
  const workDay = job.workDays.find(wd => isoDateString(wd.date) === targetISO);
  
  return workDay?.note || null;
}

// Get total duration (number of work days)
export function getJobDuration(job) {
  // Priority 1: Count explicit workDays
  if (job.workDays && job.workDays.length > 0) {
    return job.workDays.filter(wd => wd.status !== 'noWork').length;
  }
  
  // Priority 2: Use durationDays field
  return Math.max(job.durationDays || 1, 1);
}

// Check if job uses new workDays model
export function usesWorkDaysModel(job) {
  return !!(job.workDays && job.workDays.length > 0);
}

// Check if job uses legacy model
export function usesLegacyModel(job) {
  return !usesWorkDaysModel(job) && !!job.scheduledDate;
}

// Convert legacy job to workDays structure (for migration/display)
export function convertLegacyToWorkDays(job) {
  if (usesWorkDaysModel(job)) {
    return job.workDays;
  }
  
  const dates = getJobCalendarDates(job);
  
  return dates.map(date => ({
    id: isoDateString(date),
    date: date,
    status: 'planned',
    note: null,
    createdAt: new Date()
  }));
}